'use client';

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { useAuth } from '@/lib/auth';
import { authApi, API_URL } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { getPostLoginRedirect, REDIRECT_QUERY_KEY } from '@/lib/auth-redirect';
import { brand } from '@/lib/brand';
import { toast } from '@/lib/toast';
import { lookupTranslation, resolveSsoErrorI18nKey } from '@/lib/sso-errors';

/**
 * Microsoft 品牌 logo（四色方块 SVG，按 Microsoft Identity Platform 指引）
 * 不通过 next/image 走 CDN，inline 即可，体积 <300 字节
 */
function MicrosoftLogo({ size = 18 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 21 21"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export default function LoginPage() {
  const searchParams = useSearchParams();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [devLoading, setDevLoading] = useState(false);
  const [error, setError] = useState('');
  const [devEmail, setDevEmail] = useState('');
  // v2.4：密码登录折叠态。默认 collapsed；点「用密码登录」次按钮展开
  // v2.4 UI 调整（2026-05-20）：密码表单始终展开（业界标准 GitHub/Stripe 风格），
  // SSO 改为次要按钮放表单下方，降低老用户认知负担
  // v2.4：SSO 跳转中态。点击 Microsoft 按钮后立即 true，浏览器跳走前展示遮罩
  const [ssoRedirecting, setSsoRedirecting] = useState(false);
  const enableDevEmailLogin = process.env.NEXT_PUBLIC_ENABLE_DEV_EMAIL_LOGIN === 'true';

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  // 防止 React strict-mode 双调用时重复 toast
  const ssoErrorHandledRef = useRef(false);

  /**
   * v2.4：读 URL 参数 `?ssoError=<CODE>`，映射到 i18n 文案 -> showToast，
   * 然后 history.replaceState 清掉 query，避免刷新页面重复触发。
   *
   * 保留 ?redirect=... query（密码通道仍可用）。
   */
  useEffect(() => {
    if (ssoErrorHandledRef.current) return;
    const ssoErrorCode = searchParams.get('ssoError');
    if (!ssoErrorCode) return;
    ssoErrorHandledRef.current = true;

    const i18nKey = resolveSsoErrorI18nKey(ssoErrorCode);
    const message =
      lookupTranslation(t, i18nKey) || t.auth.sso.error.fallback;

    // 6s 不自动消失（用户可手动关），错误图标
    toast.error(message, { duration: 6000 });

    // 清掉 ?ssoError，保留 ?redirect=（如有），防止刷新页面重新触发 toast
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('ssoError');
      const cleanQuery = url.searchParams.toString();
      const cleanPath = `/login${cleanQuery ? `?${cleanQuery}` : ''}`;
      window.history.replaceState(null, '', cleanPath);
    }
    // searchParams 仅初次读取，依赖故意收紧
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  /**
   * v2.4：用微软账号登录 - 跳转后端 `/auth/sso/start`，浏览器进入 Microsoft 域。
   *
   * - 立即 setLoading(true) + 按钮 disabled，防止并发触发
   * - 显示全屏 spinner 中间态 + 文案 `auth.sso.status.redirecting`
   * - 把当前 ?redirect=... 透传给后端（SSO 与密码通道共用 redirect 语义）
   */
  const handleSsoLogin = () => {
    if (ssoRedirecting) return;
    setSsoRedirecting(true);
    setError('');

    // 透传 ?redirect=...：若用户从 /login?redirect=/organization/members 进来，
    // SSO 成功后回跳到 /organization/members
    const redirectParam =
      searchParams.get(REDIRECT_QUERY_KEY) ||
      // 兜底：默认登录后页（/overview），不假定后端默认
      undefined;
    const ssoStartUrl =
      `${API_URL}/auth/sso/start` +
      (redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : '');

    // 全页跳转（不是 fetch），Microsoft authorize 必须经由浏览器跳转
    window.location.href = ssoStartUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response: any = await authApi.login({
        username: formData.username,
        password: formData.password,
      });

      // 后端返回格式：{ accessToken, tokenType, expiresIn, user }
      // 需要兼容 api-client 的响应拦截器解包
      const loginData = response.data || response;
      const token = loginData.accessToken;
      const user = loginData.user;

      if (!token || !user) {
        console.error('Login response:', response);
        throw new Error('Invalid login response format');
      }

      login(token, user);

      const redirectTarget = getPostLoginRedirect(searchParams.get(REDIRECT_QUERY_KEY));

      // 使用硬刷新跳转，确保 AuthContext 能够重新加载并获取权限信息
      // 这样可以避免首次登录后需要手动刷新才能显示页面的问题
      window.location.href = redirectTarget;
    } catch (err: any) {
      // 提取错误消息，处理多种可能的格式
      let errorMessage = t.auth.login.loginFailed;

      if (err.response?.status === 401) {
        // 401 错误通常是用户名或密码错误
        errorMessage = t.auth.login.invalidCredentials || '用户名或密码错误';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message && !err.message.includes('Network Error')) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDevEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!devEmail.trim()) {
      setError(t.auth.login.devEmailRequired);
      return;
    }
    setDevLoading(true);
    setError('');
    try {
      const response: any = await authApi.devEmailLogin({
        email: devEmail.trim(),
      });
      const loginData = response.data || response;
      const token = loginData.accessToken;
      const user = loginData.user;
      if (!token || !user) {
        throw new Error('Invalid login response format');
      }
      login(token, user);
      const redirectTarget = getPostLoginRedirect(searchParams.get(REDIRECT_QUERY_KEY));
      window.location.href = redirectTarget;
    } catch (err: any) {
      let errorMessage = t.auth.login.loginFailed;
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message && !err.message.includes('Network Error')) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setDevLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-start justify-center pt-32 sm:pt-40 pb-12 px-4 sm:px-8 relative bg-black"
      data-testid="login-page"
    >
      {/* 网格背景 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.15]">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }}></div>
      </div>

      {/* 车灯效果 - 左右两侧 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* 左侧车灯 */}
        <div className="absolute top-1/3 left-0 w-[500px] h-[300px] bg-blue-400/40 rounded-full blur-3xl transform -translate-x-1/4"></div>
        {/* 右侧车灯 */}
        <div className="absolute top-1/3 right-0 w-[500px] h-[300px] bg-purple-400/40 rounded-full blur-3xl transform translate-x-1/4"></div>
      </div>

      {/* 语言切换器 */}
      <div className="absolute top-6 right-6 z-50">
        <LanguageSwitcher variant="icon" showLabel={false} />
      </div>

      {/* v2.4 UI 调整（2026-05-20）：SSO 跳转中只在按钮内显示 spinner，不再全屏遮罩
          —— 跟密码登录按钮的 loading 体验一致，避免全屏遮罩在浏览器跳走前的短暂闪烁 */}

      <div className="w-full max-w-md relative z-10">
        {/* 品牌标识 */}
        <div className="text-center mb-10 animate-fade-in">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative w-16 h-16 transform hover:scale-105 transition-transform duration-500">
              <Image
                src={brand.logoPath}
                alt={`${brand.appTitle} Logo`}
                fill
                className="object-contain brightness-0 invert"
                priority
              />
            </div>
          </div>
          <h1 className="text-3xl font-light mb-2 text-white tracking-tight">{t.auth.login.brandName}</h1>
          <p className="text-sm text-gray-300 font-light">{t.auth.login.tagline}</p>
        </div>

        {/* 登录表单 */}
        <div className="bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 p-10 shadow-2xl animate-slide-up">
            <div className="text-center mb-6">
              <h2 className="text-xl font-light text-white tracking-wide" data-testid="page-title">
                {t.auth.login.title}
              </h2>
            </div>

            {error && (
              <div
                className="mb-6 p-3 rounded-md text-sm font-light bg-red-500/10 text-red-400 border border-red-500/20"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            {/* v2.4 UI 调整（2026-05-20）：密码表单始终展开作为主登录方式（保留老用户操作习惯）*/}
            <form onSubmit={handleSubmit} className="space-y-5" data-form="login">
              <div className="group">
                <label htmlFor="username" className="block text-sm font-light mb-2 text-gray-300 uppercase tracking-wider text-xs">
                  {t.auth.login.username}
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-md text-white placeholder-gray-400 focus:border-white focus:bg-white/10 focus:outline-none transition-all duration-300 font-light"
                  placeholder={t.auth.login.usernamePlaceholder}
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                />
              </div>

              <div className="group">
                <label htmlFor="password" className="block text-sm font-light mb-2 text-gray-300 uppercase tracking-wider text-xs">
                  {t.auth.login.password}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-md text-white placeholder-gray-400 focus:border-white focus:bg-white/10 focus:outline-none transition-all duration-300 font-light"
                  placeholder={t.auth.login.passwordPlaceholder}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 text-base font-light rounded-md mt-6 transition-all duration-300 bg-white text-black hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed tracking-wide uppercase text-sm"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t.auth.login.processing}
                  </span>
                ) : (
                  t.auth.login.loginButton
                )}
              </Button>
            </form>

            {/* v2.4 UI 调整：SSO 改为次要按钮，放在密码表单下方（分隔线区分） */}
            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-white/15" />
              <span className="text-xs font-light tracking-wider text-gray-400 uppercase">{t.auth.sso.button.or}</span>
              <div className="h-px flex-1 bg-white/15" />
            </div>

            <Button
              type="button"
              onClick={handleSsoLogin}
              disabled={ssoRedirecting}
              data-action="sso-microsoft"
              data-testid="sso-microsoft-button"
              variant="outline"
              className="w-full h-11 text-base font-light rounded-md transition-all duration-300 bg-transparent text-white hover:bg-white/10 border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {ssoRedirecting ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>{t.auth.sso.status.redirecting}</span>
                </>
              ) : (
                <>
                  <MicrosoftLogo />
                  <span>{t.auth.sso.button.microsoft}</span>
                </>
              )}
            </Button>

            {enableDevEmailLogin && (
              <div className="mt-8 border-t border-white/15 pt-6">
                <p className="text-xs uppercase tracking-wider text-gray-300 mb-3">
                  {t.auth.login.devEmailLoginTitle}
                </p>
                <form onSubmit={handleDevEmailLogin} className="space-y-3">
                  <input
                    name="devEmail"
                    type="email"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-md text-white placeholder-gray-400 focus:border-white focus:bg-white/10 focus:outline-none transition-all duration-300 font-light"
                    placeholder={t.auth.login.devEmailPlaceholder}
                    value={devEmail}
                    onChange={(e) => setDevEmail(e.target.value)}
                  />
                  <Button
                    type="submit"
                    disabled={devLoading}
                    className="w-full h-10 text-sm font-light rounded-md transition-all duration-300 bg-[#3370FF] text-white hover:bg-[#295ed9] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {devLoading ? t.auth.login.processing : t.auth.login.devEmailLoginButton}
                  </Button>
                </form>
              </div>
            )}
        </div>

        {/* SLOGAN 在下方 */}
        <div className="text-center mt-8 animate-fade-in">
          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-gray-600"></div>
            <p className="text-xs font-light tracking-widest text-gray-400 uppercase whitespace-nowrap">{t.auth.login.slogan}</p>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-gray-600"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
